/**
 * A run may not TERMINATE on an appliance.
 *
 * A dishwasher is counter-height and continues a cabinet run, but it is not
 * cabinetry: its sides are unfinished and it carries no countertop. When the
 * run stops on one, the slab dead-ends on the appliance and the DW's raw side
 * faces the room — not buildable. Every such terminus needs a finished end
 * panel, which is what a real installation uses to close the run and land the
 * countertop.
 *
 * The panel is emitted as a normal `blank-panel` through `buildBlankPanel`, so
 * it is PART OF THE RUN rather than bolted onto the appliance: the run grouper
 * merges blank panels with cabinets (`collectCounterHeightItems`), the
 * countertop pass spans over it, and the derived `cabinetRuns` pick it up like
 * any other bay. (Contrast `buildIslandDwEndPanel`, which predates the run
 * model and hangs a panel off the island assembly.)
 *
 * Deliberately runs LAST, after the straight fill and the filler pass, so
 * "nothing abuts this edge" is the final truth and no cabinet is double-emitted.
 *
 * The strip it claims may sit inside a clearance-zone / corridor reservation.
 * That is correct and not an exemption invented here: a counter-height item is
 * already allowed there (`objectConflictsWithZones` checks `TALL_TYPES` only,
 * which is why the dishwasher itself sits there), and the panel is the same
 * installation at the same height. It only ever claims panel width — a full
 * cabinet in that span would still be wrong.
 */

const { INCHES_TO_METERS, MIN_FILL_CABINET_WIDTH } = require('./constants');

/**
 * An end piece is a finished panel PLUS the blocking it fastens to — 3/4"
 * panel stock cannot float on its own. The blocking is a 2x4 (1.5" x 3.5"
 * actual), so the assembly is:
 *
 *   - 3.5" (2x4 laid flat) + 3/4" panel = 4.25"  — the normal end piece
 *   - 1.5" (2x4 on edge)   + 3/4" panel = 2.25"  — the tight-space minimum
 *
 * Flat blocking is the norm; the on-edge minimum is what you fall back to when
 * 4.25" will not fit. So the width is `min(room, 4.25")` and degrades
 * naturally — a reservation with space to spare gets the full end piece, a
 * pinched one lands at or below the 2.25" minimum, and anything under that is
 * a scribe filler against the appliance opening rather than a structural end.
 */
const APPLIANCE_END_PANEL_WIDTH = 4.25 * INCHES_TO_METERS;
const APPLIANCE_END_PANEL_MIN_WIDTH = 2.25 * INCHES_TO_METERS;

/** Below this a strip is a numerical artifact, not a panel. */
const MIN_PANEL_WIDTH = 0.25 * INCHES_TO_METERS;

const EPS = 1e-3;

/**
 * Appliance types that cannot finish a run end themselves. A range has
 * finished sides and a fridge gets a surround; only the dishwasher is a bare
 * box the cabinetry is expected to close around.
 */
const NEEDS_END_PANEL = new Set(['dishwasher']);

/** Distance from `edge` (walking in `dir`) to the first thing that stops us. */
function roomBeyond(edge, dir, ranges, wallLen) {
  let limit = dir > 0 ? wallLen : 0;
  for (const r of ranges) {
    if (dir > 0) {
      if (r.start >= edge - EPS) limit = Math.min(limit, r.start);
      // Already inside one — nothing available at all.
      if (r.start < edge - EPS && r.end > edge + EPS) return 0;
    } else {
      if (r.end <= edge + EPS) limit = Math.max(limit, r.end);
      if (r.start < edge - EPS && r.end > edge + EPS) return 0;
    }
  }
  return dir > 0 ? Math.max(0, limit - edge) : Math.max(0, edge - limit);
}

/**
 * Plan the finishing panels that close a run terminating on an appliance.
 *
 * The question is NOT "how many inches to the next cabinet" — that number lies.
 * A dishwasher at the end of a kitchen run in an open-plan room measures 244"
 * to the next body, across a dining table and open floor; nothing about that
 * span wants a cabinet. What matters is whether the wall beside the appliance
 * is still inside the CABINETRY ZONE: the run's own fill bounds, or the wall
 * segment the generator's fill is working. Callers pass everything outside
 * that zone in `blockedRanges` along with the real reservations, so the two
 * cases collapse into one test.
 *
 *  - **Usable span inside the zone ≥ `MIN_FILL_CABINET_WIDTH`** — a real
 *    cabinet belongs there and the fill should have emitted one. Emit nothing;
 *    an end piece would leave the rest bare and hide that the fill missed a
 *    span.
 *  - **Usable span inside the zone, but under a cabinet** — close the whole
 *    strip, the way the filler pass closes any sub-cabinet span.
 *  - **No usable span** — the run genuinely ends here, whether because the
 *    wall beyond is reserved (clearance zone / corridor / corner box) or
 *    because the run itself stops. Build the end piece: flat blocking where it
 *    fits, degrading to the on-edge minimum and below.
 *
 * Claiming from a RESERVATION is not an exemption invented here. A
 * counter-height item is already permitted in a clearance zone —
 * `objectConflictsWithZones` checks `TALL_TYPES` only, which is exactly why
 * the dishwasher itself stands there — and the panel is the same installation
 * at the same height. Only end-piece width is ever claimed; a full cabinet in
 * that span would still be wrong.
 *
 * @param {Object} p
 * @param {number} p.wallLen  along-wall length (m)
 * @param {Array<{start:number,end:number}>} p.bodies  counter-height bodies
 *   standing on this wall (cabinets, panels) plus the appliances themselves
 * @param {Array<{start:number,end:number}>} p.blockedRanges  everything the
 *   fill may not use: real reservations (clearance zones, corner boxes,
 *   corridors, openings) AND the wall outside the cabinetry zone
 * @param {Array<{type:string,id?:string,range:{start:number,end:number}}>} p.appliances
 * @returns {Array<{start:number,end:number,applianceId?:string}>} panel
 *   segments to build, each naming the appliance it finishes — the panel is
 *   that appliance's INSTALL piece (`cabinet-runs/applianceInstall.js`), so
 *   the run can rebuild it, sweep a stale one, and carry it when the
 *   appliance's bay slides.
 */
function planApplianceEndPanels({
  wallLen,
  bodies = [],
  blockedRanges = [],
  appliances = [],
}) {
  const out = [];
  const claimed = [...bodies];
  for (const a of appliances) {
    if (!NEEDS_END_PANEL.has(a.type) || !a.range) continue;
    for (const side of ['low', 'high']) {
      const dir = side === 'low' ? -1 : 1;
      const edge = side === 'low' ? a.range.start : a.range.end;
      // The wall itself finishes the run.
      if (edge <= EPS || edge >= wallLen - EPS) continue;
      // Something already stands against this edge.
      if (
        claimed.some((r) =>
          side === 'low'
            ? Math.abs(r.end - edge) < EPS
            : Math.abs(r.start - edge) < EPS
        )
      ) {
        continue;
      }
      const toBody = roomBeyond(edge, dir, claimed, wallLen);
      if (toBody < MIN_PANEL_WIDTH) continue;
      const usable = Math.min(
        toBody,
        roomBeyond(edge, dir, blockedRanges, wallLen)
      );
      // A real cabinet fits — that span is the fill's job, not ours.
      if (usable >= MIN_FILL_CABINET_WIDTH - EPS) continue;
      // A genuinely free strip under a cabinet is used whole (the filler
      // pass's rule). Otherwise build the end piece itself: flat blocking
      // where it fits, degrading to the on-edge minimum and below.
      const width =
        usable > MIN_PANEL_WIDTH
          ? Math.min(toBody, usable)
          : Math.min(toBody, APPLIANCE_END_PANEL_WIDTH);
      if (width < MIN_PANEL_WIDTH) continue;
      const seg =
        side === 'low'
          ? { start: edge - width, end: edge }
          : { start: edge, end: edge + width };
      out.push({ ...seg, ...(a.id ? { applianceId: a.id } : {}) });
      claimed.push(seg);
    }
  }
  return out;
}

module.exports = {
  planApplianceEndPanels,
  APPLIANCE_END_PANEL_WIDTH,
  APPLIANCE_END_PANEL_MIN_WIDTH,
  NEEDS_END_PANEL,
};
